// react
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import {
  createFileRoute,
  useNavigate,
  useParams,
} from '@tanstack/react-router';

// material ui
import { Alert, alpha, Container } from '@mui/material';

// api
import { useGatewaysGetCollectionAsyncByqueryOptionsQuery } from '@/api/generated/recalc/endpoints/v1/gateways';
import { useGetAccountsQuery } from '@/api/recalc/endpoints/accountsApiSlice';
import {
  useGetBuildingByIdQuery,
  useGetBuildingsByAccountQuery,
  useGetBuildingsQuery,
  usePostSubscriptionOnBuildingMutation,
} from '@/api/recalc/endpoints/buildingApiSlice';
import { useGetJobSpecificationsQuery } from '@/api/recalc/endpoints/jobSpecificationApiSlice';
import {
  useDeleteJobSubscriptionMutation,
  useGetSubscriptionsByAccountQuery,
  useGetSubscriptionsQuery,
  usePostSubscriptionMutation,
  useUpdateSubscriptionMutation,
} from '@/api/recalc/endpoints/jobSubscriptionApiSlice';
import {
  useGetAlgorithmInputFormSpecificationByJobTypeQuery,
  useGetAlgorithmInputSpecificationByJobTypeQuery,
  useGetOccupancyPredictionAnalysisQuery,
  useLazyGetOccupancyPredictionAnalysisQuery,
} from '@/api/subscription-manager/algorithmInputSlice';
import {
  useGetActuatorPlanQuery,
  useUpdateMachineLearningModelMutation,
} from '@/api/subscription-manager/predictiveHeatControlSlice';

// shared components
import PageHeader from '@/components/layout/PageHeader';
import GatewayLoadingIndicator from '@/components/ui/progress/GatewayLoadingIndicator';

// feature-components
import AlgorithmInputAdvancedDialog from '@/features/building-optimization/components/algorithm-input/AlgorithmInputAdvancedDialog';
import AlgorithmInputSimpleDialog from '@/features/building-optimization/components/algorithm-input/AlgorithmInputSimpleDialog';
import OccupancyPredictionForm from '@/features/building-optimization/components/OccupancyPredictionForm';
import DeleteSubscriptionDialog from '@/features/building-optimization/components/subscription/DeleteSubscriptionDialog';
import JobSubscriptionCreate from '@/features/building-optimization/components/subscription/JobSubscriptionCreate';
import JobSubscriptionEdit from '@/features/building-optimization/components/subscription/JobSubscriptionEdit';
import ResetSubscriptionDialog from '@/features/building-optimization/components/subscription/ResetSubscriptionDialog';
import SubscriptionDataGrid from '@/features/building-optimization/components/subscription/SubscriptionDataGrid';

// components
import { getSelectedAccount } from '@/redux/selectedAccountSlice';
import { showSnackbar } from '@/redux/snackbarSlice';
import ErrorBoundary from '@/routes/-ErrorBoundary';

import { DateTime } from 'luxon';

function formatAndEncodeTimestampForAPI(date: Date | number): string {
  const timestamp =
    typeof date === 'number'
      ? DateTime.fromMillis(date)
      : DateTime.fromJSDate(date);
  const isoString = timestamp.toISO();

  return encodeURIComponent(isoString ?? '');
}

function extractNumberFromURL(url: string | undefined | null): number | null {
  const matches = url?.match(/(\d+)$/);

  if (matches) {
    return parseInt(matches[1], 10);
  } else {
    return null;
  }
}

export const Route = createFileRoute(
  '/$accountId/_admin/operations/predictive-heat-control/subscriptions/',
)({
  component: RouteComponent,
  errorComponent: ErrorBoundary,
  staticData: { crumb: 'glossary:subscriptions' },
});

function RouteComponent() {
  const { t } = useTranslation('buildingOptimization');
  const account = useSelector(getSelectedAccount);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [openCreateSubscription, setOpenCreateSubscription] = useState(false);
  const [openEditSubscription, setOpenEditSubscription] = useState(false);
  const [openDeleteSubscription, setOpenDeleteSubscription] = useState(false);
  const [openResetModel, setOpenResetModel] = useState(false);
  const [algorithmInputOpen, setAlgorithmInputOpen] = useState(false);
  const [algorithmInputFormOpen, setAlgorithmInputFormOpen] = useState(false);

  const { buildingName } = useParams({ strict: false });

  const [modelAnalysisDialogOpen, setModelAnalysisDialogOpen] = useState(false);
  const [generatedOccupancyPlan, setGeneratedOccupancyPlan] = useState(null);
  const [isGeneratingOccupancyAnalysis, setIsGeneratingOccupancyAnalysis] =
    useState(false);

  const { data: accounts } = useGetAccountsQuery();

  const jobType = selectedSubscription?.JobType;

  const { data: algorithmInputSpec } =
    useGetAlgorithmInputSpecificationByJobTypeQuery(
      { jobType },
      { skip: !selectedSubscription?.JobType },
    );

  const { data: algorithmInputFormSpec } =
    useGetAlgorithmInputFormSpecificationByJobTypeQuery(
      { jobType },
      { skip: !selectedSubscription?.JobType },
    );

  const {
    data: allSubscriptions,
    isLoading: isLoadingAllSubscriptions,
    refetch: refetchAllSubscriptions,
  } = useGetSubscriptionsQuery({
    skip: account.AccountId !== 1,
  });

  const {
    data: accountSubscriptions,
    isLoading: isLoadingAccountSubscriptions,
    refetch: refetchAccountSubscriptions,
  } = useGetSubscriptionsByAccountQuery(account?.AccountId, {
    skip: !account.AccountId || account.AccountId === 1,
  });

  const { data: jobSpecifications, isLoading: isLoadingJobSpecifications } =
    useGetJobSpecificationsQuery();

  const { data: building, isLoading: isLoadingBuilding } =
    useGetBuildingByIdQuery(selectedSubscription?.BuildingId, {
      skip: !selectedSubscription?.BuildingId,
    });

  useEffect(() => {
    if (!building) return;
    setSelectedBuilding(building);
  }, [building]);

  const { data: buildings } = useGetBuildingsByAccountQuery(
    account?.AccountId,
    {
      skip: !account?.AccountId || account?.AccountId === 1,
    },
  );

  const { data: allBuildings } = useGetBuildingsQuery({
    skip: account?.AccountId !== 1,
  });

  const { data: gateways, isLoading: isLoadingGateways } =
    useGatewaysGetCollectionAsyncByqueryOptionsQuery(
      { 'Account.AccountId': `eq(${selectedSubscription?.AccountId})` },
      {
        skip: !selectedSubscription?.AccountId,
      },
    );

  const { data: publishedActuatorPlan } = useGetActuatorPlanQuery(
    { id: selectedSubscription?.JobSubscriptionId },
    {
      skip:
        !selectedSubscription?.JobSubscriptionId ||
        !selectedSubscription.JobType ||
        selectedSubscription.JobType !== 'PredictiveHeatControl',
    },
  );

  const [fetchOccupancyPredictionAnalysis] =
    useLazyGetOccupancyPredictionAnalysisQuery();

  const [postSubscriptionOnBuilding] = usePostSubscriptionOnBuildingMutation();

  const occupancySourceExclusions =
    selectedSubscription?.AlgorithmInput?.predictive_occupancy_detection
      ?.general?.occupancy_source_exclusion || [];

  const { data: occupancyPrediction } = useGetOccupancyPredictionAnalysisQuery(
    {
      jobSubscriptionId: selectedSubscription?.JobSubscriptionId,
      start: formatAndEncodeTimestampForAPI(DateTime.now().toUTC().toMillis()),
      end: formatAndEncodeTimestampForAPI(
        DateTime.now().plus({ days: 7 }).toUTC().toMillis(),
      ),
      exclusions: occupancySourceExclusions,
    },
    { skip: !selectedSubscription?.JobSubscriptionId },
  );

  const [updateMachineLearningModel] = useUpdateMachineLearningModelMutation();

  const [postSubscription] = usePostSubscriptionMutation();
  const [deleteSubscription] = useDeleteJobSubscriptionMutation();
  const [updateSubscription] = useUpdateSubscriptionMutation();

  const subscriptions =
    account.AccountId === 1
      ? allSubscriptions?.filter((s) => s.JobType === 'PredictiveHeatControl')
      : accountSubscriptions?.filter(
          (s) => s.JobType === 'PredictiveHeatControl',
        );

  const handleViewSubscription = (subscription) => {
    setSelectedSubscription(subscription);
    navigate({
      to: `/$accountId/operations/predictive-heat-control/subscriptions/${encodeURIComponent(subscription.DisplayName)}`,
      state: {
        subscription: subscription,
        from: location, // store current location
      },
    });
  };

  const handleCreateSubscription = async (subscription) => {
    const result = await postSubscription(subscription);

    if (!('error' in result)) {
      dispatch(
        showSnackbar({
          message: t?.('subscription.created') || 'Subscription was created',
          severity: 'success',
          response: result.data,
        }),
      );
      const subscriptionId = extractNumberFromURL(result.data.location);

      if (subscriptionId && selectedBuilding) {
        const buildingResult = await postSubscriptionOnBuilding({
          buildingId: selectedBuilding?.BuildingId,
          subscriptionId,
        });

        if (!('error' in buildingResult)) {
          dispatch(
            showSnackbar({
              message:
                t?.('subscription.addedToBuilding', {
                  buildingName: selectedBuilding?.Name,
                }) || `Subscription was added to ${selectedBuilding?.Name}`,
              severity: 'success',
              response: buildingResult.data,
            }),
          );
        }
        // Error handled by apiErrorMiddleware (snackbar + App Insights).
      }
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  };

  const handleUpdateSubscription = async (subscription) => {
    const result = await updateSubscription({
      id: selectedSubscription.JobSubscriptionId,
      patch: subscription,
    });

    if (!('error' in result)) {
      dispatch(
        showSnackbar({
          message: 'Subscription was updated',
          severity: 'success',
          response: result.data,
        }),
      );
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  };

  const handleResetMachineLearningModel = async (subscription) => {
    setSelectedSubscription(subscription);
    setOpenResetModel(true);
  };

  const handleResetMachineLearningModelConfirmation = async (body) => {
    const resetResult = await updateMachineLearningModel({
      id: selectedSubscription.JobSubscriptionId,
      data: body,
    });

    if (!('error' in resetResult)) {
      dispatch(
        showSnackbar({
          message: 'Machine learning model reset successfully',
          severity: 'success',
        }),
      );
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
    setOpenResetModel(false);
  };

  const handleDeleteSubscriptionConfirmation = async () => {
    const deleteResult = await deleteSubscription(
      selectedSubscription.JobSubscriptionId,
    );

    if (!('error' in deleteResult)) {
      dispatch(
        showSnackbar({
          message: 'Subscription was deleted',
          severity: 'success',
          response: deleteResult.data,
        }),
      );
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
    setOpenDeleteSubscription(false);
  };

  const handleEditSubscription = (subscription) => {
    setSelectedSubscription(subscription);
    setOpenEditSubscription(true);
  };

  const handleDeleteSubscription = (subscription) => {
    setSelectedSubscription(subscription);
    setOpenDeleteSubscription(true);
  };

  const handleAlgorithmInputOpen = (subscription) => {
    setSelectedSubscription(subscription);
    setAlgorithmInputOpen(true);
  };

  const handleAlgorithmInputFormOpen = (subscription) => {
    setSelectedSubscription(subscription);
    setAlgorithmInputFormOpen(true);
  };

  const handleOccupancyPredictionAnalysisOpen = (subscription) => {
    setSelectedSubscription(subscription);
    setModelAnalysisDialogOpen(true);
  };

  const generateOccupancyPredictionAnalysis = async (formData) => {
    setIsGeneratingOccupancyAnalysis(true); // Start loading

    try {
      const result = await fetchOccupancyPredictionAnalysis({
        jobSubscriptionId: selectedSubscription.JobSubscriptionId,
        duration: formData.duration,
        filterErraticPredictions: formData.filterErraticPredictions,
        exclusions: formData.exclusions,
      });

      // Log the raw result before unwrapping or processing it.

      if (result.data) {
        setGeneratedOccupancyPlan(result.data.occupancy_plan);
      } else {
        // Error handled by apiErrorMiddleware (snackbar + App Insights).
        console.warn('No data in response');
      }
    } catch (error) {
      console.error('Error fetching occupancy prediction analysis:', error);
      setGeneratedOccupancyPlan(null);
    } finally {
      setIsGeneratingOccupancyAnalysis(false); // Stop loading
    }
  };

  return (
    <Container maxWidth={false} sx={{ py: 2 }}>
      <PageHeader title={'Subscriptions'} disableArrow />
      {!buildingName && (
        <Alert
          severity="info"
          variant="outlined"
          sx={{
            mb: 2,
            backgroundColor: alpha('#1976d2', 0.1),
            borderLeft: '4px solid',
            borderColor: 'info.main',
          }}
        >
          {t?.('alert.createSubscriptionHelperText') ||
            'To create a new subscription, select a building first.'}
        </Alert>
      )}
      <GatewayLoadingIndicator
        isLoading={
          isLoadingAllSubscriptions ||
          isLoadingAccountSubscriptions ||
          isLoadingBuilding ||
          isLoadingJobSpecifications ||
          isLoadingGateways
        }
      />
      <SubscriptionDataGrid
        subscriptions={
          subscriptions?.filter((s) => s.JobType === 'PredictiveHeatControl') ||
          []
        }
        accounts={accounts}
        onSubscriptionCreate={() => setOpenCreateSubscription(true)}
        handleAlgorithmInputFormOpen={handleAlgorithmInputFormOpen}
        handleAlgorithmInputOpen={handleAlgorithmInputOpen}
        handleOccupancyPredictionAnalysisOpen={
          handleOccupancyPredictionAnalysisOpen
        }
        handleViewSubscription={handleViewSubscription}
        handleEditSubscription={handleEditSubscription}
        handleDeleteSubscription={handleDeleteSubscription}
        handleResetMachineLearningModel={handleResetMachineLearningModel}
        refetchSubscriptions={
          account.AccountId === 1
            ? refetchAllSubscriptions
            : refetchAccountSubscriptions
        }
      />

      {!isLoadingBuilding &&
        !isLoadingGateways &&
        !isLoadingJobSpecifications && (
          <JobSubscriptionCreate
            open={openCreateSubscription}
            onClose={() => setOpenCreateSubscription(false)}
            buildings={buildings || allBuildings}
            selectedBuilding={selectedBuilding}
            setSelectedBuilding={setSelectedBuilding}
            location={selectedBuilding?.Location}
            jobSpecifications={jobSpecifications}
            isLoading={
              isLoadingBuilding ||
              isLoadingGateways ||
              isLoadingJobSpecifications
            }
            selectedAccount={account}
            submit={handleCreateSubscription}
          />
        )}

      {!isLoadingBuilding &&
        !isLoadingGateways &&
        !isLoadingJobSpecifications &&
        selectedSubscription && (
          <JobSubscriptionEdit
            key={selectedSubscription?.JobSubscriptionId ?? 'edit'}
            open={openEditSubscription}
            onClose={() => setOpenEditSubscription(false)}
            submit={handleUpdateSubscription}
            subscription={selectedSubscription}
            location={building?.Location}
            jobSpecifications={jobSpecifications}
            gateways={gateways}
          />
        )}
      {!isLoadingBuilding &&
        !isLoadingGateways &&
        !isLoadingJobSpecifications &&
        selectedSubscription && (
          <DeleteSubscriptionDialog
            key={selectedSubscription?.JobSubscriptionId ?? 'delete'}
            open={openDeleteSubscription}
            onClose={() => setOpenDeleteSubscription(false)}
            handleDeleteSubscription={handleDeleteSubscriptionConfirmation}
          />
        )}

      <ResetSubscriptionDialog
        key={selectedSubscription?.JobSubscriptionId ?? 'reset'}
        open={openResetModel}
        onClose={() => setOpenResetModel(false)}
        handleResetSubscription={handleResetMachineLearningModelConfirmation}
      />

      {selectedSubscription && (
        <AlgorithmInputAdvancedDialog
          key={selectedSubscription?.JobSubscriptionId ?? 'advanced'}
          open={algorithmInputOpen}
          onClose={() => setAlgorithmInputOpen(false)}
          subscription={selectedSubscription}
          update={handleUpdateSubscription}
          specificationData={algorithmInputSpec}
        />
      )}

      {/* <Suspense fallback={<div>Loading algorithm input dialog...</div>}> */}
      <AlgorithmInputSimpleDialog
        key={selectedSubscription?.JobSubscriptionId ?? 'simple'}
        open={algorithmInputFormOpen}
        onClose={() => setAlgorithmInputFormOpen(false)}
        subscription={selectedSubscription}
        algorithmInput={selectedSubscription?.AlgorithmInput}
        update={handleUpdateSubscription}
        specificationData={algorithmInputFormSpec}
        updateMachineLearningModel={updateMachineLearningModel}
        disableAlgorithmInputForm={false}
      />
      {/* </Suspense> */}

      <OccupancyPredictionForm
        open={modelAnalysisDialogOpen}
        onClose={() => {
          setModelAnalysisDialogOpen(false);
          setGeneratedOccupancyPlan(null);
        }}
        subscription={selectedSubscription}
        actuatorPlan={publishedActuatorPlan}
        initialPrediction={occupancyPrediction}
        occupancySourceExclusions={occupancySourceExclusions}
        generateOccupancyPredictionAnalysis={
          generateOccupancyPredictionAnalysis
        }
        setGeneratedOccupancyPlan={() => setGeneratedOccupancyPlan(null)}
        generatedOccupancyPlan={generatedOccupancyPlan}
        isGenerating={isGeneratingOccupancyAnalysis}
      />
    </Container>
  );
}
